import React from 'react';
import type { ChartSpec, NotebookChapterSpec, NotebookKpi, TableSpec } from '../../types';
import { AgentMarkdown } from '../shared/AgentMarkdown';
import { ChartRenderer } from '../ChartRenderer';
import { TableRenderer } from '../TableRenderer';
import { PinButton } from './PinButton';
import { WidgetRenderer } from './widgets/WidgetRenderer';
import type { WidgetSpec } from './widgets/widgetTypes';

export type ChapterPinHandlers = {
  isPinned: (id: string) => boolean;
  onPinChart: (chart: ChartSpec, index: number) => void;
  onUnpin: (id: string) => void;
  onPinTable: (table: TableSpec, index: number) => void;
  onPinKpi: (kpi: NotebookKpi) => void;
  onPinWidget?: (widget: WidgetSpec) => void;
  pinIdForChart: (chart: ChartSpec, index: number) => string;
  pinIdForTable: (table: TableSpec, index: number) => string;
  pinIdForKpi: (kpi: NotebookKpi) => string;
  pinIdForWidget?: (widget: WidgetSpec) => string;
};

function KpiRow({
  spec,
  pins,
}: {
  spec: NotebookChapterSpec;
  pins?: ChapterPinHandlers;
}) {
  if (!spec.kpis?.length) return null;
  return (
    <div className="maitre-kpis">
      {spec.kpis.map(kpi => {
        const pinId = pins?.pinIdForKpi(kpi);
        return (
          <div key={kpi.label} className="maitre-kpi maitre-pinnable">
            {pins && pinId ? (
              <PinButton
                pinned={pins.isPinned(pinId)}
                label={kpi.label}
                onClick={() =>
                  pins.isPinned(pinId) ? pins.onUnpin(pinId) : pins.onPinKpi(kpi)
                }
              />
            ) : null}
            <div className="maitre-kpi-value">{kpi.value}</div>
            <div className="maitre-kpi-label">{kpi.label}</div>
            {kpi.hint ? (
              <div className="maitre-kpi-hint maitre-md">
                <AgentMarkdown text={kpi.hint} />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Charts({
  spec,
  tall,
  compact,
  pins,
}: {
  spec: NotebookChapterSpec;
  tall?: boolean;
  compact?: boolean;
  pins?: ChapterPinHandlers;
}) {
  if (!spec.charts?.length) return null;
  const count = spec.charts.length;
  const height = compact || count >= 3 ? 220 : tall ? 320 : 260;
  const split = count > 1;
  return (
    <div
      className={`maitre-charts${split ? ' is-split' : ''}${
        count >= 3 ? ' is-dash' : ''
      }`}
    >
      {spec.charts.map((chart, i) => {
        const pinId = pins?.pinIdForChart(chart, i);
        return (
          <div key={`${chart.dataRef}-${i}`} className="maitre-pinnable">
            {pins && pinId ? (
              <PinButton
                pinned={pins.isPinned(pinId)}
                label={chart.title || 'chart'}
                onClick={() =>
                  pins.isPinned(pinId) ? pins.onUnpin(pinId) : pins.onPinChart(chart, i)
                }
              />
            ) : null}
            <ChartRenderer spec={chart} height={height} />
          </div>
        );
      })}
    </div>
  );
}

function Tables({
  spec,
  pins,
}: {
  spec: NotebookChapterSpec;
  pins?: ChapterPinHandlers;
}) {
  if (!spec.tables?.length) return null;
  return (
    <div className="maitre-tables">
      {spec.tables.map((table, i) => {
        const pinId = pins?.pinIdForTable(table, i);
        return (
          <div key={`${table.dataRef}-${i}`} className="maitre-pinnable">
            {pins && pinId ? (
              <PinButton
                pinned={pins.isPinned(pinId)}
                label={table.title || 'table'}
                onClick={() =>
                  pins.isPinned(pinId) ? pins.onUnpin(pinId) : pins.onPinTable(table, i)
                }
              />
            ) : null}
            <TableRenderer spec={table} />
          </div>
        );
      })}
    </div>
  );
}

function Widgets({
  spec,
  pins,
}: {
  spec: NotebookChapterSpec;
  pins?: ChapterPinHandlers;
}) {
  if (!spec.widgets?.length) return null;
  return (
    <div className="maitre-widgets">
      {spec.widgets.map((widget, i) => {
        const pinId = pins?.pinIdForWidget?.(widget);
        return (
          <div
            key={`${widget.type}-${i}`}
            className={`maitre-pinnable${
              widget.type.startsWith('horizon.') ||
              widget.type === 'live.curve' ||
              widget.type === 'live.gantt' ||
              widget.type === 'live.segbar' ||
              widget.type === 'perf.outletTable'
                ? ' is-wide'
                : ''
            }`}
          >
            {pins && pinId && pins.onPinWidget ? (
              <PinButton
                pinned={pins.isPinned(pinId)}
                label={widget.title}
                onClick={() =>
                  pins.isPinned(pinId) ? pins.onUnpin(pinId) : pins.onPinWidget?.(widget)
                }
              />
            ) : null}
            <WidgetRenderer spec={widget} />
          </div>
        );
      })}
    </div>
  );
}

/** `compact` is the side-panel density: one column, shorter charts. */
export function ChapterVisuals({
  spec,
  pins,
  compact = false,
}: {
  spec: NotebookChapterSpec;
  pins?: ChapterPinHandlers;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div className="maitre-card-compact">
        <Widgets spec={spec} pins={pins} />
        <KpiRow spec={spec} pins={pins} />
        <Charts spec={spec} compact pins={pins} />
        <Tables spec={spec} pins={pins} />
      </div>
    );
  }
  const kpis = <KpiRow spec={spec} pins={pins} />;
  const widgets = <Widgets spec={spec} pins={pins} />;
  switch (spec.template) {
    case 'kpi_row':
      return (
        <>
          {widgets}
          {kpis}
        </>
      );
    case 'trend':
    case 'mix':
    case 'heatmap':
      return (
        <>
          {widgets}
          {kpis}
          <Charts spec={spec} tall pins={pins} />
        </>
      );
    case 'ranking':
    case 'compare':
      return (
        <>
          {widgets}
          {kpis}
          <Charts spec={spec} pins={pins} />
          <Tables spec={spec} pins={pins} />
        </>
      );
    case 'table_story':
      return (
        <>
          {widgets}
          {kpis}
          <Tables spec={spec} pins={pins} />
        </>
      );
    case 'analysis':
      return (
        <>
          {kpis}
          <Charts spec={spec} tall pins={pins} />
          <Tables spec={spec} pins={pins} />
        </>
      );
    case 'multi_viz':
      return (
        <div className="maitre-multi">
          {widgets}
          {kpis}
          <Charts spec={spec} compact pins={pins} />
          <Tables spec={spec} pins={pins} />
        </div>
      );
  }
}
